import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { usePropertyContext } from "@/lib/usePropertyContext"
import {
  apiClient,
  type PerformanceSummaryResponse,
  type PerformanceInsight,
  type InsightResult,
  type SeasonProfileResponse,
  type ForecastData,
} from "@/lib/api"
import { useToast } from "@stayframe/ui"

export type PeriodPreset = "next12" | "ytd" | "this_month" | "last_month" | "this_year" | "last_year" | "custom"

export interface PeriodConfig {
  label: string
  from: string
  to: string
  compare: "yoy" | "prev"
  isForwardLooking: boolean
}

function endOfMonth(year: number, month: number): string {
  const lastDay = new Date(year, month, 0).getDate()
  return `${year}-${String(month).padStart(2, "0")}-${String(lastDay).padStart(2, "0")}`
}

function computePeriod(preset: PeriodPreset, customFrom?: string, customTo?: string): PeriodConfig {
  const now = new Date()
  const y = now.getFullYear()
  const m = now.getMonth() + 1 // 1-based
  const todayStr = now.toISOString().slice(0, 10)

  switch (preset) {
    case "ytd":
      return { label: "Year to date", from: `${y}-01-01`, to: endOfMonth(y, m), compare: "yoy", isForwardLooking: false }
    case "this_month": {
      const daysInMonth = new Date(y, m, 0).getDate()
      const isMostlyFuture = now.getDate() <= daysInMonth / 2
      return { label: "This month", from: `${y}-${String(m).padStart(2, "0")}-01`, to: endOfMonth(y, m), compare: "yoy", isForwardLooking: isMostlyFuture }
    }
    case "last_month": {
      const lmDate = new Date(y, m - 2, 1) // month is 0-based, so m-2 = previous month
      const lmY = lmDate.getFullYear()
      const lmM = lmDate.getMonth() + 1
      return { label: "Last month", from: `${lmY}-${String(lmM).padStart(2, "0")}-01`, to: endOfMonth(lmY, lmM), compare: "yoy", isForwardLooking: false }
    }
    case "this_year":
      return { label: "This year", from: `${y}-01-01`, to: `${y}-12-31`, compare: "yoy", isForwardLooking: true }
    case "last_year":
      return { label: "Last year", from: `${y - 1}-01-01`, to: `${y - 1}-12-31`, compare: "prev", isForwardLooking: false }
    case "custom": {
      const customToVal = customTo || endOfMonth(y, m)
      return { label: "Custom", from: customFrom || `${y}-01-01`, to: customToVal, compare: "yoy", isForwardLooking: customToVal > todayStr }
    }
    case "next12":
    default: {
      const fromStr = `${y}-${String(m).padStart(2, "0")}-01`
      // +11 months forward for 12 total
      const toDate = new Date(y, m + 10, 1) // m is 1-based, so m+10 in 0-based = m-1+11
      const toY = toDate.getFullYear()
      const toM = toDate.getMonth() + 1
      return { label: "Next 12 months", from: fromStr, to: endOfMonth(toY, toM), compare: "yoy", isForwardLooking: true }
    }
  }
}

export function usePerformanceData() {
  const { propertyId, property } = usePropertyContext()
  const { toast } = useToast()

  const [period, setPeriodState] = useState<PeriodPreset>("next12")
  const [customFrom, setCustomFrom] = useState<string>("")
  const [customTo, setCustomTo] = useState<string>("")

  const periodConfig = useMemo(() => computePeriod(period, customFrom, customTo), [period, customFrom, customTo])
  const { from, to } = periodConfig

  const [summary, setSummary] = useState<PerformanceSummaryResponse | null>(null)
  const [insights, setInsights] = useState<PerformanceInsight[]>([])
  const [insightResult, setInsightResult] = useState<InsightResult | null>(null)
  const [insightAiEnabled, setInsightAiEnabled] = useState(false)
  const [seasonProfile, setSeasonProfile] = useState<SeasonProfileResponse | null>(null)
  const [forecast, setForecast] = useState<ForecastData | null>(null)
  const [initialLoading, setInitialLoading] = useState(true)
  const [refreshing, setRefreshing] = useState(false)
  const [insightsLoading, setInsightsLoading] = useState(true)
  const [error, setError] = useState("")
  const hasLoadedOnce = useRef(false)

  const loadSummary = useCallback(async (silent = false) => {
    if (!propertyId) return
    // On first load show skeleton; on period change show inline refreshing indicator
    if (!silent) {
      if (!hasLoadedOnce.current) setInitialLoading(true)
      else setRefreshing(true)
      setError("")
    }
    const res = await apiClient.getPerformanceSummary(propertyId, { from, to, compare: periodConfig.compare })
    if (res.ok) {
      setSummary(res)
      hasLoadedOnce.current = true
    } else if (!silent) {
      setError(res.error || "Unable to load performance data.")
    }
    if (!silent) { setInitialLoading(false); setRefreshing(false) }
  }, [propertyId, from, to, periodConfig.compare])

  const loadInsights = useCallback(async (force = false) => {
    if (!propertyId) return
    try {
      const res = await apiClient.getPerformanceInsights(propertyId, { from, to, ...(force ? { force: true } : {}) })
      if (!res.ok) {
        setInsightAiEnabled(false)
        setInsightResult(null)
        setInsights([])
        return
      }
      if (res.insights) setInsights(res.insights)
      if (res.ai_powered && res.result) {
        setInsightResult(res.result)
        setInsightAiEnabled(true)
      } else {
        setInsightAiEnabled(false)
        setInsightResult(null)
      }
    } finally {
      setInsightsLoading(false)
    }
  }, [propertyId, from, to])

  const loadSeasonProfile = useCallback(async () => {
    if (!propertyId) return
    const res = await apiClient.getSeasonProfile(propertyId)
    if (res.ok) setSeasonProfile(res)
  }, [propertyId])

  const loadForecast = useCallback(async () => {
    if (!propertyId) return
    const res = await apiClient.getRevenueForecast(propertyId)
    if (res.ok && res.forecast) setForecast(res.forecast)
  }, [propertyId])

  useEffect(() => {
    loadSummary(false)
    loadInsights()
    loadSeasonProfile()
    loadForecast()
  }, [loadSummary, loadInsights, loadSeasonProfile, loadForecast])

  const kpis = summary?.kpis
  const trend = summary?.trend || []
  const comparisonTrend = summary?.comparison?.trend || []
  const channels = summary?.channels || []
  const pacing = summary?.pacing
  const sync = summary?.sync
  const demandBehaviour = summary?.demand_behaviour
  const currency = kpis?.revenue?.currency || "USD"
  const hasData = (kpis?.revenue?.value ?? 0) > 0 || (kpis?.bookings?.value ?? 0) > 0

  const fmtCurrency = useMemo(() => {
    const normalized = (currency || "").trim().toUpperCase()
    const safeCurrency = /^[A-Z]{3}$/.test(normalized) ? normalized : "USD"
    try {
      return new Intl.NumberFormat(undefined, { style: "currency", currency: safeCurrency, minimumFractionDigits: 0, maximumFractionDigits: 0 })
    } catch {
      return new Intl.NumberFormat(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })
    }
  }, [currency])

  const setPeriod = useCallback((preset: PeriodPreset) => {
    setPeriodState(preset)
  }, [])

  const setCustomRange = useCallback((newFrom: string, newTo: string) => {
    setCustomFrom(newFrom)
    setCustomTo(newTo)
    setPeriodState("custom")
  }, [])

  return {
    propertyId,
    property,
    from,
    to,
    period,
    periodConfig,
    setPeriod,
    setCustomRange,
    summary,
    kpis,
    trend,
    comparisonTrend,
    channels,
    pacing,
    sync,
    demandBehaviour,
    insights,
    insightResult,
    insightAiEnabled,
    insightsLoading,
    seasonProfile,
    forecast,
    loading: initialLoading,
    refreshing,
    error,
    currency,
    hasData,
    fmtCurrency,
    refreshInsights: () => loadInsights(true),
    refreshSummary: () => loadSummary(true),
  }
}
